import { vars } from '@styles/vanilla-extract/theme.css';

import i18n from 'locales/i18n';
import { Trans } from 'react-i18next';
import {
	RatioSingleBarChartItem,
	RatioSingleBarChartProps,
} from '@components/report/chart/ratio-single-bar-chart/RatioSingleBarChart';

import { TriangleChartProps } from '@components/report/chart/triangle-chart/TriangleChart';

import { YTContentSummary, YTReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { ContentYtProps } from './ContentYt';

export class ContentCommonYtAdapter {
	value: YTReportRes;

	constructor(value: YTReportRes) {
		this.value = value;
	}

	get bannerHashtags() {
		const mainHashTags = this.value?.content?.main_hashtags ?? [];

		return (mainHashTags.map((i) => i) as string[]).slice(0, 5);
	}

	get contentType(): RatioSingleBarChartProps {
		const videoPercentage = this.value?.content?.summary?.normal_video_rate ?? undefined;
		const shortsPercentage = this.value?.content?.summary?.shorts_rate ?? undefined;

		const items: RatioSingleBarChartItem[] = [
			{
				label: i18n?.t(
					'infReportYoutube:features.report.section-content.yt.content-yt.content-type.ratio-single-bar-chart.video',
				), // 동영상
				value: videoPercentage?.toString()
					? `${roundFloat(videoPercentage * 100, 'down', 1)}%`
					: '- %',
				valuePercentage: (videoPercentage ?? 0) * 100,
				color: vars.global.colors.primary[40],
			},
			{
				label: i18n?.t(
					'infReportYoutube:features.report.section-content.yt.content-yt.content-type.ratio-single-bar-chart.shorts',
				), // 쇼츠
				value: shortsPercentage?.toString()
					? `${roundFloat(shortsPercentage * 100, 'down', 1)}%`
					: '- %',
				valuePercentage: (shortsPercentage ?? 0) * 100,
				color: vars.global.colors.primary[60],
			},
		];

		return {
			desc: (
				<>
					<Trans
						// t={i18n?.t}
						i18nKey={'infReportYoutube:content.content-type.description'}
						components={[
							<span key={'feed'}>
								{i18n?.t('infReportYoutube:content.content-type.span-text', {
									videoPercentage: videoPercentage?.toString()
										? roundFloat(videoPercentage * 100, 'down', 1)
										: ' - ',
									shortsPercentage: shortsPercentage?.toString()
										? roundFloat(shortsPercentage * 100, 'down', 1)
										: ' - ',
								})}
							</span>,
						]}
					/>
					{/* 전체 콘텐츠 중에서{' '}
					<span>
						동영상은 {videoPercentage?.toString() ? roundFloat(videoPercentage * 100, 'down', 1) : ' - '}%, 쇼츠는{' '}
						{shortsPercentage?.toString() ? roundFloat(shortsPercentage * 100, 'down', 1) : ' - '}%
					</span>
					를 차지하고 있습니다. */}
				</>
			),
			items,
		};
	}

	adapt(): Pick<ContentYtProps, 'bannerHashtags' | 'contentType'> {
		return {
			bannerHashtags: this.bannerHashtags,
			contentType: this.contentType,
		};
	}
}

type KeyOfYtContentSummary = keyof Exclude<YTContentSummary, null>;

export class ContentTriangleChartAdapter {
	value: YTReportRes;
	rateField: KeyOfYtContentSummary;
	rateRankField: KeyOfYtContentSummary;

	constructor(
		value: YTReportRes,
		rateField: KeyOfYtContentSummary,
		rateRankField: KeyOfYtContentSummary,
	) {
		this.value = value;
		this.rateField = rateField;
		this.rateRankField = rateRankField;
	}

	get rate() {
		return this.value?.content?.summary?.[this.rateField] ?? 0;
	}
	get rateRank() {
		return this.value?.content?.summary?.[this.rateRankField] ?? 0;
	}

	get hasNoContent() {
		const videoCount = this.value?.content?.summary?.normal_video_count ?? 0;
		const shortsCount = this.value?.content?.summary?.shorts_count ?? 0;

		const contentCounts = {
			video: videoCount,
			shorts: shortsCount,
		} as const;

		let targetContent = '';
		if (this.rateField.includes('video')) {
			targetContent = 'video';
		} else if (this.rateField.includes('shorts')) {
			targetContent = 'shorts';
		} else {
			return false;
		}

		const targetCount = contentCounts[targetContent as 'video' | 'shorts'];

		if (!targetCount?.toString()) return false;

		return targetCount === 0;
	}

	adapt(keyword: string, unit = '개', fixedNumber = 0): TriangleChartProps {
		if (this.hasNoContent)
			return {
				desc: i18n?.t(
					'infReportYoutube:content.content-triangle-chart-adapter.no-content',
				),
				valuePercentage: undefined,
			};

		return {
			desc: (
				<>
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportYoutube:content.content-triangle-chart-adapter.description'
						}
						tOptions={{ keyword }}
						components={[
							<span key={'rate'}>
								{
									i18n?.t(unit as unknown as any, {
										number:
											fixedNumber === 0
												? round(this.rate, 'down')
												: roundFloat(this.rate, 'down', fixedNumber),
									}) as string
								}
							</span>,
							<span key={'rateRank'}>
								{i18n?.t(
									'infReportYoutube:content.content-triangle-chart-adapter.top-similar-group',
									{
										rateRank: this.rateRank ? roundFloat(this.rateRank, 'down', 2) : '-',
									},
								)}
							</span>,
						]}
					/>
					{/* {keyword}
					<br />
					<span>
						{fixedNumber === 0 ? round(this.rate, 'down') : roundFloat(this.rate, 'down', fixedNumber)} {unit}
					</span>
					로 유사 그룹 중
					<br />
					<span>상위 {this.rateRank ? roundFloat(this.rateRank, 'down', 2) : '-'}%</span>에 해당됩니다. */}
				</>
			),
			valuePercentage: this.rateRank ? this.rateRank : undefined,
		};
	}
}

export class ContentYtAdapter {
	value: YTReportRes;
	constructor(value: YTReportRes) {
		this.value = value;
	}

	adapt(): ContentYtProps {
		return {
			...new ContentCommonYtAdapter(this.value).adapt(),
			averageVideoView: new ContentTriangleChartAdapter(
				this.value,
				'avg_video_view',
				'avg_video_view_rank_in_similar_group',
			).adapt(
				i18n?.t('infReportYoutube:content.avg_video_view.text') ?? '',
				/*'동영상 조회 수는'*/ 'unit.times',
			),
			averageVideoEngagement: new ContentTriangleChartAdapter(
				this.value,
				'avg_video_real_engagement',
				'avg_video_real_engagement_rank_in_similar_group',
			).adapt(
				i18n?.t('infReportYoutube:content.avg_video_real_engagement.text') ??
					'' /* 동영상 참여율은 */,
				'unit.percentage',
				2,
			),
			averageShortsView: new ContentTriangleChartAdapter(
				this.value,
				'avg_shorts_view',
				'avg_shorts_view_rank_in_similar_group',
			).adapt(
				i18n?.t('infReportYoutube:content.avg_shorts_view.text') ??
					'' /*'쇼츠 조회 수는'*/,
				'unit.times',
			),
			averageShortsEngagement: new ContentTriangleChartAdapter(
				this.value,
				'avg_shorts_real_engagement',
				'avg_shorts_real_engagement_rank_in_similar_group',
			).adapt(
				i18n?.t('infReportYoutube:content.avg_shorts_real_engagement.text') ??
					'' /*'쇼츠 참여율은'*/,
				'unit.percentage',
				2,
			),
		};
	}
}
